import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import {
  KeyRound, Plus, X, Loader2, Save, Search, RefreshCw, AlertCircle,
  CheckCircle2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const BASE = `${API}/tender360/m0`;

// Keywords base hardcoded en el scraper (informativo, no editables)
const BASE_KEYWORDS = [
  "residuos", "limpieza", "recogida", "barrido", "RSU", "jardinería", "movilidad",
  "aparcamiento", "ZBE", "depuradora", "tratamiento residuos", "EDAR",
  "viaria", "vertedero", "arbolado", "alumbrado", "alcantarillado",
];

export default function KeywordsManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [input, setInput] = useState("");
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [testResult, setTestResult] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/keywords`);
      setKeywords(r.data?.custom || []);
      setMeta({
        updated_at: r.data?.updated_at || null,
        updated_by: r.data?.updated_by || null,
      });
    } catch (e) {
      toast.error("Error cargando keywords");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => keywords.filter(k => k.toLowerCase().includes(filter.toLowerCase())),
    [keywords, filter]
  );

  const addKeyword = () => {
    const s = input.trim();
    if (!s) return;
    if (s.length > 80) { toast.error("Máx. 80 caracteres por keyword"); return; }
    if (keywords.some(k => k.toLowerCase() === s.toLowerCase())) {
      toast.info("Esa keyword ya está en la lista");
      return;
    }
    setKeywords(prev => [s, ...prev]);
    setInput("");
  };

  const removeKeyword = (kw) => {
    setKeywords(prev => prev.filter(k => k !== kw));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${BASE}/keywords`, { custom: keywords });
      setKeywords(r.data?.custom || []);
      setMeta({
        updated_at: r.data?.updated_at || null,
        updated_by: r.data?.updated_by || null,
      });
      toast.success(`✓ ${r.data?.custom?.length || 0} keywords guardadas`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const testKeyword = async (singleKw = null) => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = { custom: singleKw ? [singleKw] : keywords };
      const r = await axios.post(`${BASE}/keywords/test`, payload);
      setTestResult({ keyword: singleKw, ...r.data });
      if (singleKw) {
        toast.success(`"${singleKw}": ${r.data.matches} coincidencias`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error testeando");
    } finally { setTesting(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); addKeyword(); }
  };

  return (
    <div className="space-y-4" data-testid="keywords-manager">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-bold text-amber-900 flex items-center gap-2">
              <KeyRound size={16}/> Keywords Pando · Scraper Tender360
            </h2>
            <p className="text-xs text-amber-800 mt-1 max-w-2xl">
              Estas palabras clave amplían la detección de los scrapers (PLACSP, BOE, TED, Euskadi).
              Una licitación se considera relevante si su título o CPV coincide con la lista base
              o con cualquiera de estas keywords personalizadas.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="text-xs bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 px-3 py-1.5 rounded-lg font-semibold inline-flex items-center gap-1"
              data-testid="keywords-reload">
              {loading ? <Loader2 size={11} className="animate-spin"/> : <RefreshCw size={11}/>} Recargar
            </button>
            <button
              onClick={save}
              disabled={saving || loading}
              data-testid="keywords-save"
              className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg font-semibold inline-flex items-center gap-1 disabled:opacity-60">
              {saving ? <Loader2 size={11} className="animate-spin"/> : <Save size={11}/>}
              Guardar cambios
            </button>
          </div>
        </div>
        {meta.updated_at && (
          <p className="text-[10px] text-amber-700 mt-2">
            Última actualización: {new Date(meta.updated_at).toLocaleString("es-ES")} · {meta.updated_by}
          </p>
        )}
      </div>

      {/* Add + filter */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-1 min-w-[260px]">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            data-testid="keywords-input"
            placeholder="Añade una keyword o expresión (Enter para añadir)"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-amber-500"
          />
          <button
            onClick={addKeyword}
            disabled={!input.trim()}
            data-testid="keywords-add"
            className="bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1">
            <Plus size={12}/> Añadir
          </button>
        </div>
        <div className="flex items-center gap-1 border border-gray-200 rounded-lg px-2 py-1.5">
          <Search size={11} className="text-gray-400"/>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filtrar lista…"
            className="text-xs outline-none w-40"
            data-testid="keywords-filter"
          />
        </div>
        <button
          onClick={() => testKeyword()}
          disabled={testing || keywords.length === 0}
          data-testid="keywords-test-all"
          className="text-xs bg-blue-50 border border-blue-300 text-blue-800 hover:bg-blue-100 px-3 py-1.5 rounded-lg font-semibold inline-flex items-center gap-1 disabled:opacity-50">
          {testing ? <Loader2 size={11} className="animate-spin"/> : <Search size={11}/>}
          Probar todas
        </button>
      </div>

      {/* Test result */}
      {testResult && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs" data-testid="keywords-test-result">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-blue-900 flex items-center gap-1.5">
              <CheckCircle2 size={13}/>
              {testResult.keyword
                ? `Test "${testResult.keyword}":`
                : "Test del set completo:"}
              <span className="font-bold">{testResult.matches}</span> coincidencias en el histórico
            </p>
            <button onClick={() => setTestResult(null)} className="text-blue-600 hover:text-blue-900"><X size={12}/></button>
          </div>
          {testResult.samples?.length > 0 && (
            <ul className="space-y-1">
              {testResult.samples.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-[11px]">
                  <span className="text-blue-700 font-mono w-12 shrink-0">{s.ai_score ?? "—"}</span>
                  <span className="text-blue-700 font-bold w-16 shrink-0 uppercase">{s.source}</span>
                  <span className="text-gray-700 truncate" title={s.title}>{s.title}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Base keywords (informativo) */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-3">
        <p className="text-[11px] uppercase font-bold text-gray-500 mb-2">
          Keywords base (hardcoded en el scraper, no editables)
        </p>
        <div className="flex flex-wrap gap-1">
          {BASE_KEYWORDS.map(k => (
            <span key={k} className="text-[10px] bg-white border border-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-mono">
              {k}
            </span>
          ))}
        </div>
      </div>

      {/* Custom keywords list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-semibold text-gray-700 flex items-center justify-between">
          <span>Keywords personalizadas ({keywords.length})</span>
          {filter && <span className="text-gray-500">Mostrando {filtered.length} de {keywords.length}</span>}
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-400">
            <Loader2 className="inline animate-spin mr-2" size={14}/> Cargando…
          </div>
        ) : keywords.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-xs">
            <AlertCircle size={20} className="mx-auto mb-2 opacity-40"/>
            Aún no hay keywords personalizadas. Añade la primera arriba.
          </div>
        ) : (
          <div className="divide-y divide-gray-100" data-testid="keywords-list">
            {filtered.map((kw) => (
              <div key={kw} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 group">
                <span className="font-mono text-xs text-gray-800 flex-1 break-all">{kw}</span>
                <button
                  onClick={() => testKeyword(kw)}
                  title="Test rápido (cuenta coincidencias)"
                  className="text-[10px] text-blue-600 hover:text-blue-900 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
                  <Search size={10}/> Probar
                </button>
                <button
                  onClick={() => removeKeyword(kw)}
                  data-testid={`keywords-del-${kw}`}
                  title="Eliminar"
                  className="text-rose-500 hover:text-rose-700 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-rose-50">
                  <X size={12}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-[10px] text-gray-500 flex items-center gap-1.5">
        <ExternalLink size={10}/>
        Tras guardar, las nuevas keywords se aplicarán en la próxima ejecución del scraper diario (07:00 UTC)
        o al lanzar manualmente &ldquo;Sincronizar PLACSP/BOE/Euskadi&rdquo;.
      </div>
    </div>
  );
}
